import React, { useEffect, useState } from "react";
import cx from "classnames";
import styles from "./Accordion.module.scss";
import Icon, { IconSizes } from "../Icon/Icon";
import { IconSetIds } from "../Icon/IconMap";

type DirectionTypes = "left" | "right" | "up" | "down";

interface IAccordionPropsBase {
    headerClassName: string;
    headerContent: React.ReactNode;
    iconId?: IconSetIds; // Default: "ChevronBack"
    iconSize?: IconSizes; // Default: "xl"
    setArrowAlignment?: "left" | "right"; // Default: "right"
    openArrowDirection?: DirectionTypes; // Default: "up"
    closedArrowDirection?: DirectionTypes; // Default: "down"
    arrowIconDirection: DirectionTypes;
    isOpenByDefault?: boolean;
    isAlwaysOpen?: boolean; // Sometimes we don't want the accordion behaviour
}

interface IAccordionPropsSelfControlled extends IAccordionPropsBase {
    isOpen?:  boolean;
    onClick?: undefined;
}

interface IAccordionPropsControlled extends IAccordionPropsBase {
    isOpen: boolean;
    onClick?: () => void;
}

type IAccordionProps = IAccordionPropsSelfControlled | IAccordionPropsControlled;

// How much clockwise rotation to get to the desired location
const rotationOffsetLookup: { [key in DirectionTypes]: number } = {
    up: 0,
    right: 90,
    down: 180,
    left: 270
};

const Accordion: React.FC<IAccordionProps> = (props) => {
    const {
        headerContent,
        headerClassName,
        isOpenByDefault,
        isAlwaysOpen,
        arrowIconDirection,
        onClick,
        children
    } = props;

    const [isStateOpen, setIsOpen] = useState(isAlwaysOpen || !!isOpenByDefault);
    const isOpen = props.isOpen === undefined ? isStateOpen : props.isOpen;

    // Arrow should default to the right
    const alignArrow = props.setArrowAlignment || "right";
    const iconId = props.iconId || "ChevronBack";
    const iconSize = props.iconSize || "xl";

    const openArrowDirection = props.openArrowDirection || "up";
    const closedArrowDirection = props.closedArrowDirection || "down";

    function calculateArrowRotation(): number {
        // normalize the arrow facing to "up"
        const normalizeArrowFacing = 0 - rotationOffsetLookup[arrowIconDirection];
        const currentFacing =
            isOpen || isAlwaysOpen ? openArrowDirection : closedArrowDirection;

        return normalizeArrowFacing + rotationOffsetLookup[currentFacing];
    }

    const [arrowRotationalOffset, setArrowRotationalOffset] = useState(
        calculateArrowRotation()
    );

    useEffect(() => {
        setArrowRotationalOffset(calculateArrowRotation());
    }, [isOpen, arrowIconDirection, openArrowDirection, closedArrowDirection]);

    return (
        <React.Fragment>
            <div
                className={cx("layout-flex-row--centered", {
                    ["cursor-pointer"]: !isAlwaysOpen,
                    [headerClassName]: !!headerClassName
                })}
                onClick={onClick ? onClick : () => !isAlwaysOpen && setIsOpen(!isOpen)}>
                {alignArrow === "right" && headerContent}
                {!isAlwaysOpen && (
                    <Icon
                        id={iconId}
                        size={iconSize}
                        className={cx(styles["icon"], {
                            ["space-m"]: alignArrow === "left"
                        })}
                        style={{
                            transform: `rotate(${arrowRotationalOffset}deg)`
                        }}
                    />
                )}
                {alignArrow === "left" && headerContent}
            </div>
            <div
                className={cx(
                    {
                        [styles["height-transition"]]: !isAlwaysOpen,
                        [styles["is-open"]]: isOpen
                    },
                    styles["child-container"]
                )}>
                {children}
            </div>
        </React.Fragment>
    );
};

export default Accordion;
